<template>
    <Modal :modal-title="title"
           :dialog="dialog"
           max-width="1600px"
           dense
           @close="close"
           @visibility="visibility($event)">
        <template slot="content">
            <FiltersBar :with-search="false"
                        :is-modal="isModal"
                        ref="filtersBar"></FiltersBar>
            <ViolationPerQuestionTable :xls-loader="xlsLoader"
                                       :pdf-loader="pdfLoader"
                                       :loader="loader"
                                       :filters="mailParams"
                                       @get-file="getFile($event, filesParams)"></ViolationPerQuestionTable>
        </template>
    </Modal>
</template>

<script>
import { mapState } from 'vuex';
import debounce from 'lodash.debounce';
import { FILTERS, FILTERS_SEARCHING } from '@/constants/filtersModelNames';
import Modal from '@/components/templates/Modal';
import FiltersBar from '@/components/widgets/tables/FiltersBar';
import ViolationPerQuestionTable from '@/components/reports/violationPerQuestion/ViolationPerQuestionTable';
import AutocompleteFilter from '@/models/filters/autocompleteFilter';
import debouncedCustomers from '@/mixins/debounced/debouncedCustomers';
import debouncedSites from '@/mixins/debounced/debouncedSites';
import debouncedLocations from '@/mixins/debounced/debouncedLocations';
import filtering from '@/mixins/filtering';
import reportsFiles from '@/mixins/reportsFiles';

export default {
    name: 'ViolationPerQuestion',
    mixins: [debouncedCustomers, debouncedSites, debouncedLocations, filtering, reportsFiles],
    components: { Modal, ViolationPerQuestionTable, FiltersBar },
    props: {
        dialog: {
            type: Boolean,
            required: true
        },
        question: {
            type: String,
            required: true
        },
        startDate: {
            type: String,
            required: true
        },
        endDate: {
            type: String,
            required: true
        }
    },
    data: () => ({
        isModal: true,
        loader: false,
        debouncedFetchViolationsPerQuestion: null
    }),
    computed: {
        ...mapState('filters', [
            'modalFilters',
            'modalFiltersSearching'
        ]),
        title () {
            return `${this.$t('labels.question_report')}: ${this.question}`;
        },
        modalFiltersConfig () {
            return [
                new AutocompleteFilter({
                    model: FILTERS.CUSTOMER_ID,
                    label: this.$t('labels.customer'),
                    loading: true,
                    multiple: true,
                    search: FILTERS_SEARCHING.CUSTOMER,
                    lazy: this.getCustomersLazyLoading,
                    referralCallback: this.referralCustomerCallback,
                    searchCallback: this.debouncedGetCustomers,
                    items: {
                        obj: 'getters',
                        module: 'customers',
                        prop: 'listingCustomers'
                    }
                }),
                new AutocompleteFilter({
                    model: FILTERS.SITE_ID,
                    label: this.$t('labels.site'),
                    loading: false,
                    multiple: true,
                    disabled: true,
                    successMessages: this.$t('messages.choose_customer'),
                    search: FILTERS_SEARCHING.SITE_PER_CUSTOMER,
                    lazy: this.getSitesLazyLoading,
                    referralCallback: this.referralSiteCallback,
                    searchCallback: this.debouncedGetSites,
                    searchOptions: {
                        reference: FILTERS.CUSTOMER_ID
                    },
                    items: {
                        obj: 'getters',
                        module: 'sites',
                        prop: 'sites'
                    }
                }),
                new AutocompleteFilter({
                    model: FILTERS.LOCATION_ID,
                    label: this.$t('labels.location'),
                    loading: false,
                    multiple: true,
                    disabled: true,
                    successMessages: this.$t('messages.choose_site'),
                    search: FILTERS_SEARCHING.LOCATION,
                    lazy: this.getLocationsLazyLoading,
                    searchCallback: this.debouncedGetLocations,
                    searchOptions: {
                        reference: FILTERS.SITE_ID
                    },
                    items: {
                        obj: 'getters',
                        module: 'locations',
                        prop: 'locations'
                    }
                })
            ];
        },
        filesParams () {
            return {
                kind: 'customer_irregularities',
                question: this.question,
                start_time: this.startDate,
                end_time: this.endDate,
                ...this.generateFiltersForRequest()
            };
        },
        mailParams () {
            const params = {
                question: this.question,
                start_time: this.startDate,
                end_time: this.endDate,
                filter: {}
            };

            this.modalFiltersConfig.forEach(filter => {
                const f = filter.model;

                if (this.modalFilters[f] && this.modalFilters[f].length > 0) {
                    params.filter[f] = this.modalFilters[f].join(',');
                }
            });

            return params;
        }
    },
    watch: {
        dialog: {
            handler (val) {
                if (val) {
                    this.debouncedFetchViolationsPerQuestion = debounce(this.fetchViolationsPerQuestion, 1000);
                    this.setFilters();
                    this.setFiltersWatcher(this.debouncedFetchViolationsPerQuestion);
                }
            },
            immediate: true
        }
    },
    methods: {
        close () {
            this.$emit('close');
            this.$store.dispatch('filters/clearModalFiltersParams');
            this.$refs.filtersBar.clearFilters();
        },
        visibility (event) {
            this.$emit('visibility', event);
        },
        setFilters () {
            this.$store.commit('filters/SET_MODAL_CONFIG', this.modalFiltersConfig);
            this.getCustomers()
                .then(() => {
                    this.modalFiltersConfig.find(el => el.model === FILTERS.CUSTOMER_ID).loading = false;
                    this.$store.commit('filters/SET_MODAL_CONFIG', this.modalFiltersConfig);
                });
        },
        generateFiltersForRequest () {
            const params = {};

            this.modalFiltersConfig.forEach(filter => {
                const f = filter.model;

                if (this.modalFilters[f] && this.modalFilters[f].length > 0) {
                    params[`filter[${f}]`] = this.modalFilters[f].join(',');
                }
            });

            return params;
        },
        fetchViolationsPerQuestion () {
            this.loader = true;

            const params = {
                question: this.question,
                start_time: this.startDate,
                end_time: this.endDate,
                ...this.generateFiltersForRequest()
            };

            this.$store.dispatch('reports/getViolationsPerQuestion', params)
                .finally(() => {
                    this.loader = false;
                });
        }
    }
};
</script>

<style scoped>

</style>
